import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  UserIcon,
  MusicalNoteIcon,
  WrenchScrewdriverIcon,
  TrophyIcon,
  UserGroupIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  EyeIcon,
  PrinterIcon,
  CloudArrowDownIcon,
  PlusIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PencilIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface PersonalInfo {
  name: string;
  title: string;
  email: string;
  phone: string;
  location: string;
  website: string;
  bio: string;
  avatar: string;
  socialMedia: SocialMedia[];
}

interface SocialMedia {
  platform: string;
  handle: string;
}

interface PortfolioItem {
  type: 'audio' | 'video' | 'image';
  file: string;
  title: string;
  description: string;
  date: string;
}

interface Skill {
  name: string;
  level: number;
}

interface SkillCategory {
  name: string;
  skills: Skill[];
}

interface Award {
  title: string;
  issuer: string;
  date: string;
  description: string;
}

interface Collaboration {
  title: string;
  partner: string;
  date: string;
  description: string;
}

interface Education {
  institution: string;
  degree: string;
  startDate: string;
  endDate: string;
  current: boolean;
  description: string;
}

interface Experience {
  company: string;
  position: string;
  startDate: string;
  endDate: string;
  current: boolean;
  description: string;
}

interface ResumeData {
  theme: string;
  personalInfo: PersonalInfo;
  portfolio: PortfolioItem[];
  skills: SkillCategory[];
  awards: Award[];
  collaborations: Collaboration[];
  education: Education[];
  experience: Experience[];
}

interface ResumeSectionItem {
  id: string;
  name: string;
  nameEn: string;
  icon: React.ComponentType<any>;
}

// 翻译数据
const translations = {
  zh: {
    title: "音乐人简历",
    subtitle: "创建专业的音乐人简历，展示您的才华与成就",
    print: "打印",
    save: "保存",
    themeTitle: "简历主题",
    
    // 操作按钮
    addSocialMedia: "添加社交媒体",
    addSkillCategory: "添加技能类别",
    addSkill: "添加技能",
    addAward: "添加奖项",
    addCollaboration: "添加合作项目",
    addEducation: "添加教育经历",
    addExperience: "添加工作经验",
    chooseFiles: "选择文件",
    openPreview: "在新窗口打开预览",
    exportPdf: "导出PDF",
    
    // 节目
    sectionPersonal: "个人信息",
    sectionPortfolio: "作品集",
    sectionSkills: "专业技能",
    sectionAwards: "荣誉奖项",
    sectionCollaborations: "合作项目",
    sectionEducation: "教育背景",
    sectionExperience: "工作经验",
    sectionPreview: "简历预览",
    
    // 主题
    themeUnderwater: "紫色",
    themeBlue: "蓝色",
    themeGreen: "绿色",
    themeOrange: "橙色",
    themePink: "粉色",
    
    // 字段标签
    fieldName: "姓名",
    fieldTitle: "职业头衔",
    fieldEmail: "电子邮箱",
    fieldPhone: "电话号码",
    fieldLocation: "所在城市",
    fieldWebsite: "个人网站",
    fieldBio: "个人简介",
    fieldPlatform: "平台",
    fieldHandle: "用户名或链接",
    fieldWorkTitle: "作品标题",
    fieldWorkDesc: "作品描述",
    fieldPublishDate: "发布日期",
    fieldSkillCategory: "技能类别",
    fieldSkillName: "技能名称",
    fieldSkillLevel: "熟练度",
    fieldAwardTitle: "奖项名称",
    fieldAwardIssuer: "颁发机构",
    fieldAwardDate: "获奖日期",
    fieldAwardDesc: "奖项描述",
    fieldCollabTitle: "合作项目名称",
    fieldCollabPartner: "合作方",
    fieldCollabDate: "合作日期",
    fieldCollabDesc: "合作内容",
    fieldEduInstitution: "学校名称",
    fieldEduDegree: "学位/专业",
    fieldEduDesc: "学习内容",
    fieldExpCompany: "公司/机构名称",
    fieldExpPosition: "职位",
    fieldExpDesc: "工作内容",
    fieldStartDate: "起始日期",
    fieldEndDate: "结束日期",
    fieldCurrent: "至今",
    
    // 占位符文本
    placeholderName: "您的姓名",
    placeholderTitle: "如：电子音乐制作人/编曲师",
    placeholderEmail: "您的邮箱地址",
    placeholderPhone: "您的联系电话",
    placeholderLocation: "如：北京",
    placeholderWebsite: "如：https://yourmusic.com",
    placeholderBio: "简单介绍自己的音乐风格、理念和目标",
    placeholderHandle: "用户名或链接",
    placeholderWorkTitle: "作品名称",
    placeholderWorkDesc: "描述作品的风格、灵感或背景故事",
    placeholderSkillCategory: "如：音乐制作软件",
    placeholderSkillName: "如：Ableton Live",
    placeholderAwardTitle: "如：最佳电子音乐制作人",
    placeholderAwardIssuer: "如：中国电子音乐节",
    placeholderAwardDesc: "简要描述奖项内容或重要性",
    placeholderCollabTitle: "如：《夏日电子》专辑",
    placeholderCollabPartner: "如：李宇春、周杰伦",
    placeholderCollabDesc: "描述您在合作中的角色和贡献",
    placeholderEduInstitution: "如：中央音乐学院",
    placeholderEduDegree: "如：音乐制作学士",
    placeholderEduDesc: "描述相关课程、成就或学习重点",
    placeholderExpCompany: "如：环球音乐",
    placeholderExpPosition: "如：音乐制作人",
    placeholderExpDesc: "描述您的职责、成就和项目",
    
    // 社交平台
    platformWeibo: "微博",
    platformWechat: "微信公众号",
    platformDouyin: "抖音",
    platformBilibili: "哔哩哔哩",
    platformNetease: "网易云音乐",
    platformQqmusic: "QQ音乐",
    platformInstagram: "Instagram",
    platformYoutube: "YouTube",
    platformSoundcloud: "SoundCloud",
    platformSpotify: "Spotify",
    platformOther: "其他平台",
    
    // 上传和预览
    uploadTitle: "上传作品",
    uploadDesc: "拖放或点击上传音频、视频或图片文件",
    uploadFormats: "支持的格式: MP3, WAV, MP4, JPG, PNG",
    previewComingSoon: "预览功能开发中，敬请期待！",
    previewDescription: '点击"在新窗口打开预览"以查看优化后的简历',
    
    // 默认和演示数据
    clickEditName: "点击编辑姓名",
    defaultTitle: "音乐制作人/创作者",
    socialMedia: "社交媒体",
    noWorks: "您尚未添加任何作品",
    
    // 演示数据 - 个人信息
    demoName: "陈艺轩",
    demoProfTitle: "电子音乐制作人/作曲家",
    demoEmail: "chen@music-producer.com",
    demoPhone: "+86 138 8888 8888",
    demoLocation: "上海",
    demoWebsite: "www.chenyx-music.com",
    demoBio: "热爱电子音乐和实验性声音设计的制作人。专注于将环境音效与现代电子元素融合，创造沉浸式听觉体验。曾为多个独立游戏和短片创作配乐，希望通过音乐传递情感和故事。",
    demoWechat: "音乐艺轩",
    demoBilibili: "陈艺轩音乐",
    demoNetease: "陈艺轩_Music",
    
    // 作品集演示
    demoPortfolio1Title: "《电子梦境》单曲",
    demoPortfolio1Desc: "结合氛围音乐与电子节拍的原创单曲，探索内心深处的声音景观。",
    demoPortfolio2Title: "《城市回声》MV",
    demoPortfolio2Desc: "以城市声音为素材，创作的视听作品，记录都市中的声音记忆。",
    demoPortfolio3Title: "《声波》EP封面",
    demoPortfolio3Desc: "为个人EP《声波》设计的专辑封面，表达声音的可视化形态。",
    
    // 技能演示
    demoSkillsSoftware: "音乐制作软件",
    demoSkillsMusic: "音乐技能",
    demoSkillsInstruments: "乐器演奏",
    demoSkillsComposition: "作曲/编曲",
    demoSkillsSoundDesign: "声音设计",
    demoSkillsMixing: "混音/母带处理",
    demoSkillsPiano: "钢琴",
    demoSkillsGuitar: "吉他",
    demoSkillsSynthesizer: "电子合成器",
    demoSkillAbleton: "Ableton Live",
    demoSkillFL: "FL Studio",
    demoSkillLogic: "Logic Pro X",
    
    // 奖项演示
    demoAward1Title: "最佳电子音乐制作人",
    demoAward1Issuer: "中国电子音乐节",
    demoAward1Date: "2023-09",
    demoAward1Desc: "凭借单曲《电子梦境》获得年度最佳电子音乐制作人奖项。",
    demoAward2Title: "最佳原创游戏配乐",
    demoAward2Issuer: "中国独立游戏联盟",
    demoAward2Date: "2023-05",
    demoAward2Desc: "为独立游戏《幻境》创作的原创配乐获得最佳音乐奖。",
    
    // 合作演示
    demoCollab1Title: "《城市声波》合辑",
    demoCollab1Partner: "王力宏、陈奕迅",
    demoCollab1Date: "2023-07",
    demoCollab1Desc: "参与华语音乐人合辑项目，负责两首歌曲的制作和编曲工作。",
    demoCollab2Title: "《无界》广告配乐",
    demoCollab2Partner: "华为科技",
    demoCollab2Date: "2023-03",
    demoCollab2Desc: "为华为新品发布会创作主题音乐，结合东方元素与现代电子音乐。",
    
    // 教育演示
    demoEdu1Institution: "中央音乐学院",
    demoEdu1Degree: "音乐制作与录音艺术 硕士",
    demoEdu1StartDate: "2018-09",
    demoEdu1EndDate: "2021-06",
    demoEdu1Desc: "专注于电子音乐制作与声音设计，师从著名音乐制作人李教授。",
    demoEdu2Institution: "上海音乐学院",
    demoEdu2Degree: "作曲与作曲技术理论 学士",
    demoEdu2StartDate: "2014-09",
    demoEdu2EndDate: "2018-06",
    demoEdu2Desc: "学习作曲基础理论与技术，同时参与多个音乐创作项目。",
    
    // 经验演示
    demoExp1Company: "环球音乐集团",
    demoExp1Position: "音乐制作人",
    demoExp1StartDate: "2022-03",
    demoExp1EndDate: "",
    demoExp1Current: "true",
    demoExp1Desc: "负责公司旗下艺人的音乐制作、编曲和混音工作，参与多个商业专辑的制作。合作艺人包括多位一线流行歌手，提供专业的音乐制作服务。",
    demoExp2Company: "光线传媒",
    demoExp2Position: "配乐师",
    demoExp2StartDate: "2021-07",
    demoExp2EndDate: "2022-02",
    demoExp2Current: "false",
    demoExp2Desc: "为公司出品的多部影视作品创作配乐，包括两部网剧和一部院线电影，负责从概念到最终混音的全过程。"
  },
  
  en: {
    title: "Musician Resume",
    subtitle: "Create professional musician resume to showcase your talent and achievements",
    print: "Print",
    save: "Save",
    themeTitle: "Resume Theme",
    
    // Action buttons
    addSocialMedia: "Add Social Media",
    addSkillCategory: "Add Skill Category",
    addSkill: "Add Skill",
    addAward: "Add Award",
    addCollaboration: "Add Collaboration",
    addEducation: "Add Education",
    addExperience: "Add Experience",
    chooseFiles: "Choose Files",
    openPreview: "Open Preview in New Window",
    exportPdf: "Export PDF",
    
    // Sections
    sectionPersonal: "Personal Info",
    sectionPortfolio: "Portfolio",
    sectionSkills: "Skills",
    sectionAwards: "Awards",
    sectionCollaborations: "Collaborations",
    sectionEducation: "Education",
    sectionExperience: "Experience",
    sectionPreview: "Preview",
    
    // Themes
    themeUnderwater: "Purple",
    themeBlue: "Blue",
    themeGreen: "Green",
    themeOrange: "Orange",
    themePink: "Pink",
    
    // Field labels
    fieldName: "Name",
    fieldTitle: "Professional Title",
    fieldEmail: "Email",
    fieldPhone: "Phone",
    fieldLocation: "Location",
    fieldWebsite: "Website",
    fieldBio: "Bio",
    fieldPlatform: "Platform",
    fieldHandle: "Username or Link",
    fieldWorkTitle: "Work Title",
    fieldWorkDesc: "Work Description",
    fieldPublishDate: "Publish Date",
    fieldSkillCategory: "Skill Category",
    fieldSkillName: "Skill Name",
    fieldSkillLevel: "Proficiency",
    fieldAwardTitle: "Award Title",
    fieldAwardIssuer: "Issuer",
    fieldAwardDate: "Award Date",
    fieldAwardDesc: "Award Description",
    fieldCollabTitle: "Collaboration Title",
    fieldCollabPartner: "Partner",
    fieldCollabDate: "Collaboration Date",
    fieldCollabDesc: "Collaboration Content",
    fieldEduInstitution: "Institution",
    fieldEduDegree: "Degree/Major",
    fieldEduDesc: "Study Content",
    fieldExpCompany: "Company/Organization",
    fieldExpPosition: "Position",
    fieldExpDesc: "Work Content",
    fieldStartDate: "Start Date",
    fieldEndDate: "End Date",
    fieldCurrent: "Current",
    
    // Placeholder texts
    placeholderName: "Your Name",
    placeholderTitle: "e.g. Electronic Music Producer/Composer",
    placeholderEmail: "Your Email Address",
    placeholderPhone: "Your Phone Number",
    placeholderLocation: "e.g. New York",
    placeholderWebsite: "e.g. https://yourmusic.com",
    placeholderBio: "Brief introduction of your musical style, philosophy and goals",
    placeholderHandle: "Username or Link",
    placeholderWorkTitle: "Work Name",
    placeholderWorkDesc: "Describe the style, inspiration or background story of the work",
    placeholderSkillCategory: "e.g. Music Production Software",
    placeholderSkillName: "e.g. Ableton Live",
    placeholderAwardTitle: "e.g. Best Electronic Music Producer",
    placeholderAwardIssuer: "e.g. Electronic Music Festival",
    placeholderAwardDesc: "Brief description of award content or importance",
    placeholderCollabTitle: "e.g. Summer Electronic Album",
    placeholderCollabPartner: "e.g. Taylor Swift, Ed Sheeran",
    placeholderCollabDesc: "Describe your role and contribution in the collaboration",
    placeholderEduInstitution: "e.g. Berklee College of Music",
    placeholderEduDegree: "e.g. Bachelor of Music Production",
    placeholderEduDesc: "Describe relevant courses, achievements or study focus",
    placeholderExpCompany: "e.g. Universal Music",
    placeholderExpPosition: "e.g. Music Producer",
    placeholderExpDesc: "Describe your responsibilities, achievements and projects",
    
    // Social platforms
    platformWeibo: "Weibo",
    platformWechat: "WeChat Official Account",
    platformDouyin: "TikTok",
    platformBilibili: "Bilibili",
    platformNetease: "NetEase Cloud Music",
    platformQqmusic: "QQ Music",
    platformInstagram: "Instagram",
    platformYoutube: "YouTube",
    platformSoundcloud: "SoundCloud",
    platformSpotify: "Spotify",
    platformOther: "Other Platform",
    
    // Upload and preview
    uploadTitle: "Upload Works",
    uploadDesc: "Drag and drop or click to upload audio, video or image files",
    uploadFormats: "Supported formats: MP3, WAV, MP4, JPG, PNG",
    previewComingSoon: "Preview feature is coming soon!",
    previewDescription: 'Click "Open Preview in New Window" to view the optimized resume',
    
    // Defaults and demos
    clickEditName: "Click to Edit Name",
    defaultTitle: "Music Producer/Creator",
    socialMedia: "Social Media",
    noWorks: "You haven't added any works yet",
    
    // Demo data - Personal Info
    demoName: "Alex Chen",
    demoProfTitle: "Electronic Music Producer/Composer",
    demoEmail: "alex@music-producer.com",
    demoPhone: "+1 555 888 8888",
    demoLocation: "New York",
    demoWebsite: "www.alexchen-music.com",
    demoBio: "A music producer passionate about electronic music and experimental sound design. Focused on blending ambient sounds with modern electronic elements to create immersive auditory experiences. Has composed soundtracks for multiple indie games and short films, hoping to convey emotions and stories through music.",
    demoWechat: "Music Alex",
    demoBilibili: "Alex Chen Music",
    demoNetease: "Alex_Music",
    
    // Portfolio demo
    demoPortfolio1Title: "Electronic Dreams Single",
    demoPortfolio1Desc: "An original single combining ambient music with electronic beats, exploring the inner soundscape.",
    demoPortfolio2Title: "City Echoes MV",
    demoPortfolio2Desc: "An audiovisual work created using city sounds as material, recording the sonic memories of urban life.",
    demoPortfolio3Title: "Sound Waves EP Cover",
    demoPortfolio3Desc: "Album cover designed for personal EP 'Sound Waves', expressing the visual form of sound.",
    
    // Skills demo
    demoSkillsSoftware: "Music Production Software",
    demoSkillsMusic: "Music Skills",
    demoSkillsInstruments: "Instrument Performance",
    demoSkillsComposition: "Composition/Arrangement",
    demoSkillsSoundDesign: "Sound Design",
    demoSkillsMixing: "Mixing/Mastering",
    demoSkillsPiano: "Piano",
    demoSkillsGuitar: "Guitar",
    demoSkillsSynthesizer: "Electronic Synthesizer",
    demoSkillAbleton: "Ableton Live",
    demoSkillFL: "FL Studio",
    demoSkillLogic: "Logic Pro X",
    
    // Awards demo
    demoAward1Title: "Best Electronic Music Producer",
    demoAward1Issuer: "Electronic Music Festival",
    demoAward1Date: "2023-09",
    demoAward1Desc: "Awarded Best Electronic Music Producer of the Year for the single 'Electronic Dreams'.",
    demoAward2Title: "Best Original Game Soundtrack",
    demoAward2Issuer: "Independent Game Alliance",
    demoAward2Date: "2023-05",
    demoAward2Desc: "Won Best Music Award for original soundtrack created for indie game 'Fantasy Realm'.",
    
    // Collaborations demo
    demoCollab1Title: "City Sound Waves Compilation",
    demoCollab1Partner: "Leehom Wang, Eason Chan",
    demoCollab1Date: "2023-07",
    demoCollab1Desc: "Participated in musicians' compilation project, responsible for production and arrangement of two songs.",
    demoCollab2Title: "Boundless Advertisement Music",
    demoCollab2Partner: "Huawei Technology",
    demoCollab2Date: "2023-03",
    demoCollab2Desc: "Created theme music for product launch, combining Eastern elements with modern electronic music.",
    
    // Education demo
    demoEdu1Institution: "Central Conservatory of Music",
    demoEdu1Degree: "Master of Music Production and Recording Arts",
    demoEdu1StartDate: "2018-09",
    demoEdu1EndDate: "2021-06",
    demoEdu1Desc: "Focused on electronic music production and sound design, studied under renowned music producer Professor Li.",
    demoEdu2Institution: "Shanghai Conservatory of Music",
    demoEdu2Degree: "Bachelor of Composition and Composition Technology Theory",
    demoEdu2StartDate: "2014-09",
    demoEdu2EndDate: "2018-06",
    demoEdu2Desc: "Studied fundamental composition theory and techniques while participating in multiple music creation projects.",
    
    // Experience demo
    demoExp1Company: "Universal Music Group",
    demoExp1Position: "Music Producer",
    demoExp1StartDate: "2022-03",
    demoExp1EndDate: "",
    demoExp1Current: "true",
    demoExp1Desc: "Responsible for music production, arrangement and mixing for company artists, participated in multiple commercial album productions. Collaborated with several top-tier pop singers, providing professional music production services.",
    demoExp2Company: "Enlight Media",
    demoExp2Position: "Composer",
    demoExp2StartDate: "2021-07",
    demoExp2EndDate: "2022-02",
    demoExp2Current: "false",
    demoExp2Desc: "Created soundtracks for multiple film and TV productions by the company, including two web series and one theatrical film, responsible for the entire process from concept to final mixing."
  }
};

const MusicianResumeEditor: React.FC = () => {
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState<string>('personal');
  const [dragOver, setDragOver] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 翻译函数
  const t = useCallback((key: string): string => {
    const value = translations[language as 'zh' | 'en'][key as keyof typeof translations[typeof language]];
    return String(value || key);
  }, [language]);

  // 生成演示数据函数
  const generateDemoData = useCallback((): ResumeData => ({
    theme: 'purple',
    personalInfo: {
      name: t('demoName'),
      title: t('demoProfTitle'),
      email: t('demoEmail'),
      phone: t('demoPhone'),
      location: t('demoLocation'),
      website: t('demoWebsite'),
      bio: t('demoBio'),
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex',
      socialMedia: [
        { platform: 'wechat', handle: t('demoWechat') },
        { platform: 'netease', handle: t('demoNetease') },
        { platform: 'bilibili', handle: t('demoBilibili') }
      ]
    },
    portfolio: [
      {
        type: 'audio',
        file: '',
        title: t('demoPortfolio1Title'),
        description: t('demoPortfolio1Desc'),
        date: '2023-10-15'
      },
      {
        type: 'video',
        file: '',
        title: t('demoPortfolio2Title'),
        description: t('demoPortfolio2Desc'),
        date: '2023-08-21'
      },
      {
        type: 'image',
        file: '',
        title: t('demoPortfolio3Title'),
        description: t('demoPortfolio3Desc'),
        date: '2023-06-10'
      }
    ],
    skills: [
      {
        name: t('demoSkillsSoftware'),
        skills: [
          { name: t('demoSkillAbleton'), level: 95 },
          { name: t('demoSkillFL'), level: 85 },
          { name: t('demoSkillLogic'), level: 80 }
        ]
      },
      {
        name: t('demoSkillsMusic'),
        skills: [
          { name: t('demoSkillsComposition'), level: 90 },
          { name: t('demoSkillsSoundDesign'), level: 85 },
          { name: t('demoSkillsMixing'), level: 80 }
        ]
      },
      {
        name: t('demoSkillsInstruments'),
        skills: [
          { name: t('demoSkillsPiano'), level: 75 },
          { name: t('demoSkillsGuitar'), level: 65 },
          { name: t('demoSkillsSynthesizer'), level: 90 }
        ]
      }
    ],
    awards: [
      {
        title: t('demoAward1Title'),
        issuer: t('demoAward1Issuer'),
        date: t('demoAward1Date'),
        description: t('demoAward1Desc')
      },
      {
        title: t('demoAward2Title'),
        issuer: t('demoAward2Issuer'),
        date: t('demoAward2Date'),
        description: t('demoAward2Desc')
      }
    ],
    collaborations: [
      {
        title: t('demoCollab1Title'),
        partner: t('demoCollab1Partner'),
        date: t('demoCollab1Date'),
        description: t('demoCollab1Desc')
      },
      {
        title: t('demoCollab2Title'),
        partner: t('demoCollab2Partner'),
        date: t('demoCollab2Date'),
        description: t('demoCollab2Desc')
      }
    ],
    education: [
      {
        institution: t('demoEdu1Institution'),
        degree: t('demoEdu1Degree'),
        startDate: t('demoEdu1StartDate'),
        endDate: t('demoEdu1EndDate'),
        current: false,
        description: t('demoEdu1Desc')
      },
      {
        institution: t('demoEdu2Institution'),
        degree: t('demoEdu2Degree'),
        startDate: t('demoEdu2StartDate'),
        endDate: t('demoEdu2EndDate'),
        current: false,
        description: t('demoEdu2Desc')
      }
    ],
    experience: [
      {
        company: t('demoExp1Company'),
        position: t('demoExp1Position'),
        startDate: t('demoExp1StartDate'),
        endDate: t('demoExp1EndDate'),
        current: t('demoExp1Current') === 'true',
        description: t('demoExp1Desc')
      },
      {
        company: t('demoExp2Company'),
        position: t('demoExp2Position'),
        startDate: t('demoExp2StartDate'),
        endDate: t('demoExp2EndDate'),
        current: t('demoExp2Current') === 'true',
        description: t('demoExp2Desc')
      }
    ]
  }), [t]);

  // 简历部分导航
  const resumeSections: ResumeSectionItem[] = useMemo(() => [
    { id: 'personal', name: t('sectionPersonal'), nameEn: 'Personal Info', icon: UserIcon },
    { id: 'portfolio', name: t('sectionPortfolio'), nameEn: 'Portfolio', icon: MusicalNoteIcon },
    { id: 'skills', name: t('sectionSkills'), nameEn: 'Skills', icon: WrenchScrewdriverIcon },
    { id: 'awards', name: t('sectionAwards'), nameEn: 'Awards', icon: TrophyIcon },
    { id: 'collaborations', name: t('sectionCollaborations'), nameEn: 'Collaborations', icon: UserGroupIcon },
    { id: 'education', name: t('sectionEducation'), nameEn: 'Education', icon: AcademicCapIcon },
    { id: 'experience', name: t('sectionExperience'), nameEn: 'Experience', icon: BriefcaseIcon },
    { id: 'preview', name: t('sectionPreview'), nameEn: 'Preview', icon: EyeIcon }
  ], [t]);

  // 简历主题
  const resumeThemes = useMemo(() => [
    { id: 'purple', name: t('themeUnderwater'), nameEn: 'Purple', color: '#8a2be2', gradient: 'from-purple-600 to-purple-700' },
    { id: 'blue', name: t('themeBlue'), nameEn: 'Blue', color: '#0078ff', gradient: 'from-blue-600 to-blue-700' },
    { id: 'green', name: t('themeGreen'), nameEn: 'Green', color: '#00b894', gradient: 'from-green-600 to-green-700' },
    { id: 'orange', name: t('themeOrange'), nameEn: 'Orange', color: '#fd7e14', gradient: 'from-orange-600 to-orange-700' },
    { id: 'pink', name: t('themePink'), nameEn: 'Pink', color: '#e83e8c', gradient: 'from-pink-600 to-pink-700' }
  ], [t]);

  // 初始化简历数据
  const [resumeData, setResumeData] = useState<ResumeData>(() => generateDemoData());

  // 当语言切换时更新演示数据
  React.useEffect(() => {
    setResumeData(generateDemoData());
  }, [generateDemoData]);

  // 获取当前主题
  const currentTheme = useMemo(() => {
    return resumeThemes.find(theme => theme.id === resumeData.theme) || resumeThemes[0];
  }, [resumeData.theme, resumeThemes]);

  // 更新简历数据
  const updateResumeData = useCallback((path: string[], value: any) => {
    setResumeData(prev => {
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      
      current[path[path.length - 1]] = value;
      return newData;
    });
  }, []);

  // 添加社交媒体
  const addSocialMedia = useCallback(() => {
    const newSocialMedia = [...resumeData.personalInfo.socialMedia, { platform: 'wechat', handle: '' }];
    updateResumeData(['personalInfo', 'socialMedia'], newSocialMedia);
  }, [resumeData.personalInfo.socialMedia, updateResumeData]);

  // 删除社交媒体
  const removeSocialMedia = useCallback((index: number) => {
    const newSocialMedia = resumeData.personalInfo.socialMedia.filter((_, i) => i !== index);
    updateResumeData(['personalInfo', 'socialMedia'], newSocialMedia);
  }, [resumeData.personalInfo.socialMedia, updateResumeData]);

  // 头像上传
  const handleAvatarUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          updateResumeData(['personalInfo', 'avatar'], event.target?.result as string);
        };
        reader.readAsDataURL(file);
      }
    };
    
    input.click();
  }, [updateResumeData]);

  // 文件拖拽处理
  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    processFiles(files);
  }, []);

  // 文件选择处理
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      processFiles(files);
    }
  }, []);

  // 处理文件
  const processFiles = useCallback((files: FileList) => {
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        addPortfolioItem(file, result);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  // 添加作品集项目
  const addPortfolioItem = useCallback((file: File, fileData: string) => {
    const fileType = file.type.split('/')[0] as 'audio' | 'video' | 'image';
    const fileName = file.name;
    
    const newItem: PortfolioItem = {
      type: fileType === 'audio' || fileType === 'video' || fileType === 'image' ? fileType : 'image',
      file: fileData,
      title: fileName.split('.')[0],
      description: '',
      date: new Date().toISOString().split('T')[0]
    };
    
    const newPortfolio = [...resumeData.portfolio, newItem];
    updateResumeData(['portfolio'], newPortfolio);
  }, [resumeData.portfolio, updateResumeData]);

  // 删除作品集项目
  const removePortfolioItem = useCallback((index: number) => {
    const newPortfolio = resumeData.portfolio.filter((_, i) => i !== index);
    updateResumeData(['portfolio'], newPortfolio);
  }, [resumeData.portfolio, updateResumeData]);

  // 移动数组项目
  const moveItem = useCallback(<T,>(array: T[], fromIndex: number, toIndex: number): T[] => {
    if (toIndex < 0 || toIndex >= array.length) return array;
    const newArray = [...array];
    const item = newArray[fromIndex];
    newArray.splice(fromIndex, 1);
    newArray.splice(toIndex, 0, item);
    return newArray;
  }, []);

  // 添加技能类别
  const addSkillCategory = useCallback(() => {
    const newSkills = [...resumeData.skills, { name: '', skills: [] }];
    updateResumeData(['skills'], newSkills);
  }, [resumeData.skills, updateResumeData]);

  // 删除技能类别
  const removeSkillCategory = useCallback((index: number) => {
    const newSkills = resumeData.skills.filter((_, i) => i !== index);
    updateResumeData(['skills'], newSkills);
  }, [resumeData.skills, updateResumeData]);

  // 添加技能
  const addSkill = useCallback((categoryIndex: number) => {
    const newSkills = [...resumeData.skills];
    newSkills[categoryIndex].skills.push({ name: '', level: 50 });
    updateResumeData(['skills'], newSkills);
  }, [resumeData.skills, updateResumeData]);

  // 删除技能
  const removeSkill = useCallback((categoryIndex: number, skillIndex: number) => {
    const newSkills = [...resumeData.skills];
    newSkills[categoryIndex].skills = newSkills[categoryIndex].skills.filter((_, i) => i !== skillIndex);
    updateResumeData(['skills'], newSkills);
  }, [resumeData.skills, updateResumeData]);

  // 通用添加项目函数
  const addItem = useCallback((section: keyof ResumeData, item: any) => {
    const newArray = [...(resumeData[section] as any[]), item];
    updateResumeData([section], newArray);
  }, [resumeData, updateResumeData]);

  // 通用删除项目函数
  const removeItem = useCallback((section: keyof ResumeData, index: number) => {
    const newArray = (resumeData[section] as any[]).filter((_, i) => i !== index);
    updateResumeData([section], newArray);
  }, [resumeData, updateResumeData]);

  // 添加奖项
  const addAward = useCallback(() => addItem('awards', { title: '', issuer: '', date: '', description: '' }), [addItem]);
  
  // 添加合作项目
  const addCollaboration = useCallback(() => addItem('collaborations', { title: '', partner: '', date: '', description: '' }), [addItem]);
  
  // 添加教育经历
  const addEducation = useCallback(() => addItem('education', { institution: '', degree: '', startDate: '', endDate: '', current: false, description: '' }), [addItem]);
  
  // 添加工作经验
  const addExperience = useCallback(() => addItem('experience', { company: '', position: '', startDate: '', endDate: '', current: false, description: '' }), [addItem]);

  // 打开预览窗口
  const openPreviewWindow = useCallback(() => {
    alert(t('previewComingSoon'));
  }, [t]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('title')}</h1>
            <p className="text-lg text-gray-600">{t('subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
              <PrinterIcon className="w-5 h-5" />
              <span>{t('print')}</span>
            </button>
            <button className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md">
              <CloudArrowDownIcon className="w-5 h-5" />
              <span>{t('save')}</span>
            </button>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Left Sidebar */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden sticky top-4">
              {/* Profile Section */}
              <div className={`relative text-white px-6 py-8 bg-gradient-to-br ${currentTheme.gradient}`}>
                <div className="text-center">
                  <div className="relative w-24 h-24 mx-auto mb-4 group cursor-pointer" onClick={handleAvatarUpload}>
                    <div className="w-full h-full rounded-full overflow-hidden border-3 border-white/30 shadow-lg">
                      {resumeData.personalInfo.avatar ? (
                        <img src={resumeData.personalInfo.avatar} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-white/20 flex items-center justify-center">
                          <UserIcon className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <div className="absolute bottom-0 right-0 bg-white/80 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <PencilIcon className="w-3 h-3 text-gray-700" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold">{resumeData.personalInfo.name || t('clickEditName')}</h2>
                  <p className="text-white/90">{resumeData.personalInfo.title || t('defaultTitle')}</p>
                </div>
              </div>

              {/* Navigation */}
              <div className="p-4">
                <nav className="space-y-2">
                  {resumeSections.map(section => {
                    const IconComponent = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                          activeSection === section.id
                            ? `bg-gradient-to-r ${currentTheme.gradient} text-white shadow-md`
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <IconComponent className="w-5 h-5" />
                        <span className="font-medium">{section.name}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Theme Section */}
              <div className="border-t p-4">
                <h3 className="text-sm font-bold text-gray-900 mb-3">{t('themeTitle')}</h3>
                <div className="flex flex-wrap gap-2">
                  {resumeThemes.map(theme => (
                    <button
                      key={theme.id}
                      onClick={() => updateResumeData(['theme'], theme.id)}
                      className={`flex flex-col items-center p-2 rounded-lg transition-all ${
                        resumeData.theme === theme.id ? 'ring-2 ring-offset-2 ring-gray-300' : ''
                      }`}
                    >
                      <div
                        className="w-6 h-6 rounded-full mb-1"
                        style={{ backgroundColor: theme.color }}
                      />
                      <span className="text-xs text-gray-600">{theme.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Personal Info */}
            {activeSection === 'personal' && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-xl font-bold text-gray-900">{t('sectionPersonal')}</h3>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldName')}</label>
                      <input
                        type="text"
                        value={resumeData.personalInfo.name}
                        onChange={(e) => updateResumeData(['personalInfo', 'name'], e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder={t('placeholderName')}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldTitle')}</label>
                      <input
                        type="text"
                        value={resumeData.personalInfo.title}
                        onChange={(e) => updateResumeData(['personalInfo', 'title'], e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder={t('placeholderTitle')}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldEmail')}</label>
                      <input
                        type="email"
                        value={resumeData.personalInfo.email}
                        onChange={(e) => updateResumeData(['personalInfo', 'email'], e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder={t('placeholderEmail')}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldPhone')}</label>
                      <input
                        type="tel"
                        value={resumeData.personalInfo.phone}
                        onChange={(e) => updateResumeData(['personalInfo', 'phone'], e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder={t('placeholderPhone')}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldLocation')}</label>
                      <input
                        type="text"
                        value={resumeData.personalInfo.location}
                        onChange={(e) => updateResumeData(['personalInfo', 'location'], e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder={t('placeholderLocation')}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldWebsite')}</label>
                      <input
                        type="url"
                        value={resumeData.personalInfo.website}
                        onChange={(e) => updateResumeData(['personalInfo', 'website'], e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder={t('placeholderWebsite')}
                      />
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldBio')}</label>
                    <textarea
                      rows={4}
                      value={resumeData.personalInfo.bio}
                      onChange={(e) => updateResumeData(['personalInfo', 'bio'], e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder={t('placeholderBio')}
                    />
                  </div>

                  {/* Social Media */}
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-4">{t('socialMedia')}</h4>
                    
                    <div className="space-y-4 mb-4">
                      {resumeData.personalInfo.socialMedia.map((platform, index) => (
                        <div key={index} className="flex gap-4 items-end">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldPlatform')}</label>
                            <select
                              value={platform.platform}
                              onChange={(e) => {
                                const newSocialMedia = [...resumeData.personalInfo.socialMedia];
                                newSocialMedia[index].platform = e.target.value;
                                updateResumeData(['personalInfo', 'socialMedia'], newSocialMedia);
                              }}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                            >
                              <option value="weibo">{t('platformWeibo')}</option>
                              <option value="wechat">{t('platformWechat')}</option>
                              <option value="douyin">{t('platformDouyin')}</option>
                              <option value="bilibili">{t('platformBilibili')}</option>
                              <option value="netease">{t('platformNetease')}</option>
                              <option value="qqmusic">{t('platformQqmusic')}</option>
                              <option value="instagram">{t('platformInstagram')}</option>
                              <option value="youtube">{t('platformYoutube')}</option>
                              <option value="soundcloud">{t('platformSoundcloud')}</option>
                              <option value="spotify">{t('platformSpotify')}</option>
                              <option value="other">{t('platformOther')}</option>
                            </select>
                          </div>
                          
                          <div className="flex-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldHandle')}</label>
                            <input
                              type="text"
                              value={platform.handle}
                              onChange={(e) => {
                                const newSocialMedia = [...resumeData.personalInfo.socialMedia];
                                newSocialMedia[index].handle = e.target.value;
                                updateResumeData(['personalInfo', 'socialMedia'], newSocialMedia);
                              }}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                              placeholder={t('placeholderHandle')}
                            />
                          </div>
                          
                          <button
                            onClick={() => removeSocialMedia(index)}
                            className="flex-shrink-0 p-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <XMarkIcon className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <button
                      onClick={addSocialMedia}
                      className="flex items-center gap-2 px-4 py-2 text-blue-600 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <PlusIcon className="w-5 h-5" />
                      <span>{t('addSocialMedia')}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Portfolio */}
            {activeSection === 'portfolio' && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-xl font-bold text-gray-900">{t('sectionPortfolio')}</h3>
                </div>
                
                <div className="p-6">
                  {/* Upload Section */}
                  <div
                    className={`border-2 border-dashed rounded-xl p-8 text-center mb-8 transition-all ${
                      dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
                    }`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                    onDrop={handleFileDrop}
                  >
                    <DocumentArrowUpIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{t('uploadTitle')}</h4>
                    <p className="text-gray-600 mb-2">{t('uploadDesc')}</p>
                    <p className="text-sm text-gray-500 mb-6">{t('uploadFormats')}</p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      accept=".mp3,.wav,.mp4,.jpg,.jpeg,.png"
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
                    >
                      {t('chooseFiles')}
                    </button>
                  </div>
                  
                  {/* Portfolio List */}
                  <div className="space-y-6">
                    {resumeData.portfolio.map((item, index) => (
                      <div key={index} className="flex gap-6 p-6 border border-gray-200 rounded-xl bg-gray-50">
                        {/* Preview */}
                        <div className="w-40 h-32 bg-gray-200 rounded-lg flex-shrink-0 flex items-center justify-center">
                          {item.type === 'audio' && (
                            <MusicalNoteIcon className="w-12 h-12 text-gray-400" />
                          )}
                          {item.type === 'video' && (
                            <div className="text-gray-400 text-3xl">📹</div>
                          )}
                          {item.type === 'image' && item.file && (
                            <img src={item.file} alt={item.title} className="w-full h-full object-cover rounded-lg" />
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldWorkTitle')}</label>
                            <input
                              type="text"
                              value={item.title}
                              onChange={(e) => {
                                const newPortfolio = [...resumeData.portfolio];
                                newPortfolio[index].title = e.target.value;
                                updateResumeData(['portfolio'], newPortfolio);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderWorkTitle')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldWorkDesc')}</label>
                            <textarea
                              rows={3}
                              value={item.description}
                              onChange={(e) => {
                                const newPortfolio = [...resumeData.portfolio];
                                newPortfolio[index].description = e.target.value;
                                updateResumeData(['portfolio'], newPortfolio);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderWorkDesc')}
                            />
                          </div>
                          
                          <div className="flex gap-4">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldPublishDate')}</label>
                              <input
                                type="date"
                                value={item.date}
                                onChange={(e) => {
                                  const newPortfolio = [...resumeData.portfolio];
                                  newPortfolio[index].date = e.target.value;
                                  updateResumeData(['portfolio'], newPortfolio);
                                }}
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                if (index > 0) {
                                  const newPortfolio = moveItem(resumeData.portfolio, index, index - 1);
                                  updateResumeData(['portfolio'], newPortfolio);
                                }
                              }}
                              disabled={index === 0}
                              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ArrowUpIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (index < resumeData.portfolio.length - 1) {
                                  const newPortfolio = moveItem(resumeData.portfolio, index, index + 1);
                                  updateResumeData(['portfolio'], newPortfolio);
                                }
                              }}
                              disabled={index === resumeData.portfolio.length - 1}
                              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ArrowDownIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removePortfolioItem(index)}
                              className="p-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {resumeData.portfolio.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <MusicalNoteIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>{t('noWorks')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Skills */}
            {activeSection === 'skills' && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-xl font-bold text-gray-900">{t('sectionSkills')}</h3>
                </div>
                
                <div className="p-6">
                  <div className="space-y-6 mb-6">
                    {resumeData.skills.map((category, catIndex) => (
                      <div key={catIndex} className="p-6 border border-gray-200 rounded-xl bg-gray-50">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldSkillCategory')}</label>
                            <input
                              type="text"
                              value={category.name}
                              onChange={(e) => {
                                const newSkills = [...resumeData.skills];
                                newSkills[catIndex].name = e.target.value;
                                updateResumeData(['skills'], newSkills);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderSkillCategory')}
                            />
                          </div>
                          
                          <button
                            onClick={() => removeSkillCategory(catIndex)}
                            className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <XMarkIcon className="w-5 h-5" />
                          </button>
                        </div>
                        
                        <div className="space-y-4 mb-4">
                          {category.skills.map((skill, skillIndex) => (
                            <div key={skillIndex} className="flex gap-4 items-end p-4 bg-white rounded-lg">
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldSkillName')}</label>
                                <input
                                  type="text"
                                  value={skill.name}
                                  onChange={(e) => {
                                    const newSkills = [...resumeData.skills];
                                    newSkills[catIndex].skills[skillIndex].name = e.target.value;
                                    updateResumeData(['skills'], newSkills);
                                  }}
                                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder={t('placeholderSkillName')}
                                />
                              </div>
                              
                              <div className="w-48">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  {t('fieldSkillLevel')} ({skill.level}%)
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  step="5"
                                  value={skill.level}
                                  onChange={(e) => {
                                    const newSkills = [...resumeData.skills];
                                    newSkills[catIndex].skills[skillIndex].level = Number(e.target.value);
                                    updateResumeData(['skills'], newSkills);
                                  }}
                                  className="w-full"
                                />
                              </div>
                              
                              <button
                                onClick={() => removeSkill(catIndex, skillIndex)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        <button
                          onClick={() => addSkill(catIndex)}
                          className="flex items-center gap-2 px-4 py-2 text-blue-600 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>{t('addSkill')}</span>
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={addSkillCategory}
                    className="flex items-center gap-2 px-6 py-3 text-blue-600 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>{t('addSkillCategory')}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Awards */}
            {activeSection === 'awards' && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-xl font-bold text-gray-900">{t('sectionAwards')}</h3>
                </div>
                
                <div className="p-6">
                  <div className="space-y-6 mb-6">
                    {resumeData.awards.map((award, index) => (
                      <div key={index} className="p-6 border border-gray-200 rounded-xl bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldAwardTitle')}</label>
                            <input
                              type="text"
                              value={award.title}
                              onChange={(e) => {
                                const newAwards = [...resumeData.awards];
                                newAwards[index].title = e.target.value;
                                updateResumeData(['awards'], newAwards);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderAwardTitle')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldAwardIssuer')}</label>
                            <input
                              type="text"
                              value={award.issuer}
                              onChange={(e) => {
                                const newAwards = [...resumeData.awards];
                                newAwards[index].issuer = e.target.value;
                                updateResumeData(['awards'], newAwards);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderAwardIssuer')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldAwardDate')}</label>
                            <input
                              type="month"
                              value={award.date}
                              onChange={(e) => {
                                const newAwards = [...resumeData.awards];
                                newAwards[index].date = e.target.value;
                                updateResumeData(['awards'], newAwards);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldAwardDesc')}</label>
                          <textarea
                            rows={2}
                            value={award.description}
                            onChange={(e) => {
                              const newAwards = [...resumeData.awards];
                              newAwards[index].description = e.target.value;
                              updateResumeData(['awards'], newAwards);
                            }}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={t('placeholderAwardDesc')}
                          />
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              if (index > 0) {
                                const newAwards = moveItem(resumeData.awards, index, index - 1);
                                updateResumeData(['awards'], newAwards);
                              }
                            }}
                            disabled={index === 0}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ArrowUpIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (index < resumeData.awards.length - 1) {
                                const newAwards = moveItem(resumeData.awards, index, index + 1);
                                updateResumeData(['awards'], newAwards);
                              }
                            }}
                            disabled={index === resumeData.awards.length - 1}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ArrowDownIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeItem('awards', index)}
                            className="p-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={addAward}
                    className="flex items-center gap-2 px-6 py-3 text-blue-600 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>{t('addAward')}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Collaborations */}
            {activeSection === 'collaborations' && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-xl font-bold text-gray-900">{t('sectionCollaborations')}</h3>
                </div>
                
                <div className="p-6">
                  <div className="space-y-6 mb-6">
                    {resumeData.collaborations.map((collab, index) => (
                      <div key={index} className="p-6 border border-gray-200 rounded-xl bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldCollabTitle')}</label>
                            <input
                              type="text"
                              value={collab.title}
                              onChange={(e) => {
                                const newCollaborations = [...resumeData.collaborations];
                                newCollaborations[index].title = e.target.value;
                                updateResumeData(['collaborations'], newCollaborations);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderCollabTitle')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldCollabPartner')}</label>
                            <input
                              type="text"
                              value={collab.partner}
                              onChange={(e) => {
                                const newCollaborations = [...resumeData.collaborations];
                                newCollaborations[index].partner = e.target.value;
                                updateResumeData(['collaborations'], newCollaborations);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderCollabPartner')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldCollabDate')}</label>
                            <input
                              type="month"
                              value={collab.date}
                              onChange={(e) => {
                                const newCollaborations = [...resumeData.collaborations];
                                newCollaborations[index].date = e.target.value;
                                updateResumeData(['collaborations'], newCollaborations);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldCollabDesc')}</label>
                          <textarea
                            rows={2}
                            value={collab.description}
                            onChange={(e) => {
                              const newCollaborations = [...resumeData.collaborations];
                              newCollaborations[index].description = e.target.value;
                              updateResumeData(['collaborations'], newCollaborations);
                            }}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={t('placeholderCollabDesc')}
                          />
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              if (index > 0) {
                                const newCollaborations = moveItem(resumeData.collaborations, index, index - 1);
                                updateResumeData(['collaborations'], newCollaborations);
                              }
                            }}
                            disabled={index === 0}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ArrowUpIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (index < resumeData.collaborations.length - 1) {
                                const newCollaborations = moveItem(resumeData.collaborations, index, index + 1);
                                updateResumeData(['collaborations'], newCollaborations);
                              }
                            }}
                            disabled={index === resumeData.collaborations.length - 1}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ArrowDownIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeItem('collaborations', index)}
                            className="p-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={addCollaboration}
                    className="flex items-center gap-2 px-6 py-3 text-blue-600 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>{t('addCollaboration')}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Education */}
            {activeSection === 'education' && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-xl font-bold text-gray-900">{t('sectionEducation')}</h3>
                </div>
                
                <div className="p-6">
                  <div className="space-y-6 mb-6">
                    {resumeData.education.map((edu, index) => (
                      <div key={index} className="p-6 border border-gray-200 rounded-xl bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldEduInstitution')}</label>
                            <input
                              type="text"
                              value={edu.institution}
                              onChange={(e) => {
                                const newEducation = [...resumeData.education];
                                newEducation[index].institution = e.target.value;
                                updateResumeData(['education'], newEducation);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderEduInstitution')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldEduDegree')}</label>
                            <input
                              type="text"
                              value={edu.degree}
                              onChange={(e) => {
                                const newEducation = [...resumeData.education];
                                newEducation[index].degree = e.target.value;
                                updateResumeData(['education'], newEducation);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderEduDegree')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldStartDate')}</label>
                            <input
                              type="month"
                              value={edu.startDate}
                              onChange={(e) => {
                                const newEducation = [...resumeData.education];
                                newEducation[index].startDate = e.target.value;
                                updateResumeData(['education'], newEducation);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldEndDate')}</label>
                            <div className="flex gap-2 items-center">
                              <input
                                type="month"
                                value={edu.endDate}
                                onChange={(e) => {
                                  const newEducation = [...resumeData.education];
                                  newEducation[index].endDate = e.target.value;
                                  updateResumeData(['education'], newEducation);
                                }}
                                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={edu.current}
                              />
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={edu.current}
                                  onChange={(e) => {
                                    const newEducation = [...resumeData.education];
                                    newEducation[index].current = e.target.checked;
                                    if (e.target.checked) {
                                      newEducation[index].endDate = '';
                                    }
                                    updateResumeData(['education'], newEducation);
                                  }}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span>{t('fieldCurrent')}</span>
                              </label>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldEduDesc')}</label>
                          <textarea
                            rows={2}
                            value={edu.description}
                            onChange={(e) => {
                              const newEducation = [...resumeData.education];
                              newEducation[index].description = e.target.value;
                              updateResumeData(['education'], newEducation);
                            }}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={t('placeholderEduDesc')}
                          />
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              if (index > 0) {
                                const newEducation = moveItem(resumeData.education, index, index - 1);
                                updateResumeData(['education'], newEducation);
                              }
                            }}
                            disabled={index === 0}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ArrowUpIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (index < resumeData.education.length - 1) {
                                const newEducation = moveItem(resumeData.education, index, index + 1);
                                updateResumeData(['education'], newEducation);
                              }
                            }}
                            disabled={index === resumeData.education.length - 1}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ArrowDownIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeItem('education', index)}
                            className="p-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={addEducation}
                    className="flex items-center gap-2 px-6 py-3 text-blue-600 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>{t('addEducation')}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Experience */}
            {activeSection === 'experience' && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-xl font-bold text-gray-900">{t('sectionExperience')}</h3>
                </div>
                
                <div className="p-6">
                  <div className="space-y-6 mb-6">
                    {resumeData.experience.map((exp, index) => (
                      <div key={index} className="p-6 border border-gray-200 rounded-xl bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldExpCompany')}</label>
                            <input
                              type="text"
                              value={exp.company}
                              onChange={(e) => {
                                const newExperience = [...resumeData.experience];
                                newExperience[index].company = e.target.value;
                                updateResumeData(['experience'], newExperience);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderExpCompany')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldExpPosition')}</label>
                            <input
                              type="text"
                              value={exp.position}
                              onChange={(e) => {
                                const newExperience = [...resumeData.experience];
                                newExperience[index].position = e.target.value;
                                updateResumeData(['experience'], newExperience);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder={t('placeholderExpPosition')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldStartDate')}</label>
                            <input
                              type="month"
                              value={exp.startDate}
                              onChange={(e) => {
                                const newExperience = [...resumeData.experience];
                                newExperience[index].startDate = e.target.value;
                                updateResumeData(['experience'], newExperience);
                              }}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldEndDate')}</label>
                            <div className="flex gap-2 items-center">
                              <input
                                type="month"
                                value={exp.endDate}
                                onChange={(e) => {
                                  const newExperience = [...resumeData.experience];
                                  newExperience[index].endDate = e.target.value;
                                  updateResumeData(['experience'], newExperience);
                                }}
                                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={exp.current}
                              />
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={exp.current}
                                  onChange={(e) => {
                                    const newExperience = [...resumeData.experience];
                                    newExperience[index].current = e.target.checked;
                                    if (e.target.checked) {
                                      newExperience[index].endDate = '';
                                    }
                                    updateResumeData(['experience'], newExperience);
                                  }}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span>{t('fieldCurrent')}</span>
                              </label>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('fieldExpDesc')}</label>
                          <textarea
                            rows={3}
                            value={exp.description}
                            onChange={(e) => {
                              const newExperience = [...resumeData.experience];
                              newExperience[index].description = e.target.value;
                              updateResumeData(['experience'], newExperience);
                            }}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={t('placeholderExpDesc')}
                          />
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              if (index > 0) {
                                const newExperience = moveItem(resumeData.experience, index, index - 1);
                                updateResumeData(['experience'], newExperience);
                              }
                            }}
                            disabled={index === 0}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ArrowUpIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (index < resumeData.experience.length - 1) {
                                const newExperience = moveItem(resumeData.experience, index, index + 1);
                                updateResumeData(['experience'], newExperience);
                              }
                            }}
                            disabled={index === resumeData.experience.length - 1}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ArrowDownIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeItem('experience', index)}
                            className="p-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={addExperience}
                    className="flex items-center gap-2 px-6 py-3 text-blue-600 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>{t('addExperience')}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Preview */}
            {activeSection === 'preview' && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900">{t('sectionPreview')}</h3>
                  <div className="flex gap-3">
                    <button
                      onClick={openPreviewWindow}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <EyeIcon className="w-5 h-5" />
                      <span>{t('openPreview')}</span>
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                      <PrinterIcon className="w-5 h-5" />
                      <span>{t('print')}</span>
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                      <CloudArrowDownIcon className="w-5 h-5" />
                      <span>{t('exportPdf')}</span>
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <EyeIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-lg text-gray-600 mb-4">{t('previewDescription')}</p>
                    <img 
                      src="https://api.dicebear.com/7.x/shapes/svg?seed=preview" 
                      alt="Preview placeholder" 
                      className="mx-auto max-w-md opacity-60"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MusicianResumeEditor;